import { useEffect, useRef, useState } from 'react';
import { useInView } from 'motion/react';
import { Wrapper, Number, Suffix, Label } from './styles';

interface StatCounterProps {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  duration?: number;
}

/**
 * Número que conta do zero até `value` quando entra no viewport.
 * Usa easing exponencial para sensação cinematográfica.
 */
export function StatCounter({
  value,
  prefix = '',
  suffix = '',
  label,
  duration = 2000,
}: StatCounterProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!isInView || hasStarted.current) return;
    hasStarted.current = true;

    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Easing expo out
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.round(eased * value));

      if (progress < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }, [isInView, value, duration]);

  return (
    <Wrapper ref={ref}>
      <Number>
        {prefix && <Suffix>{prefix}</Suffix>}
        {count.toLocaleString('pt-BR')}
        {suffix && <Suffix>{suffix}</Suffix>}
      </Number>
      <Label>{label}</Label>
    </Wrapper>
  );
}
